/**
 * The captain's speech bubble — the run-time half of the feature.
 *
 * A line-for-line port of the shipped `KingSpeech` runtime described in
 * `reports/captain/PORT-SPEC.md` §5.1, with the spec's own corrections applied: the
 * reveal is **12 ms per alphanumeric character and 3 ms for everything else** (the
 * 45 ms in the brief is a stale comment in the source), there is **no queue** (a new
 * line hard-replaces the current one after snapping it to complete), and the hold
 * scales with length so a long line is never cut mid-read.
 *
 * **Two timers, no rAF.** `_typeTimer` walks the string; `_hideTimer` fades it. The
 * element is built once and never removed, and the per-line hot path writes exactly
 * three things: `textNode.nodeValue`, `style.opacity`, `style.transform`. Nothing
 * allocates per character.
 *
 * **The forced reflow is load-bearing.** `_show` writes the "before" state, reads
 * `offsetWidth`, then writes the "after" state. Drop the read and the browser
 * coalesces both writes into one style recalculation and no transition plays at all.
 *
 * **No portrait in here, deliberately.** The art pipeline measured it twice: at 1×
 * (64 CSS px) a portrait collapses to two smudges and carries no identity, and the
 * bubble has room for nothing larger on a 390 px phone. So the run-time presence is
 * the *voice* plus a name plate in `--font-pixel` — the recall hook, legible, repeated
 * thirty-odd times across a run — and the face is spent once, at 3×, on the death
 * card where it is a person you could pick out of a line-up.
 *
 * **It never eats a touch.** `pointer-events: none` on the layer and on the bubble.
 * On a phone bullet-heaven that is not negotiable.
 */

import { composePortrait, paintPortraitToCanvas, recompose, type Portrait } from '../captain/portrait';
import type { Captain } from '../captain/seed';
import { div, el, span } from './dom';

/** Post-reveal dwell floor. */
const HOLD_MS_MIN = 2500;
/** Fade in and out. */
const FADE_MS = 260;
/** Delay after a character matching `/[A-Za-z0-9]/`. */
const CHAR_MS = 12;
/** Delay after anything else — space, comma, period, dash. Nearly instant. */
const PUNCT_MS = 3;

const ALNUM = /[A-Za-z0-9]/;

/**
 * A long line must never vanish mid-read, so the dwell scales with length. Verbatim
 * from the source: `max(2500, 1400 + len * 40)`.
 */
export function computeHoldMs(text: string): number {
  return Math.max(HOLD_MS_MIN, 1400 + text.length * 40);
}

/** Total life of a line, for tests and for the death beat's own scheduling. */
export function lineLifetimeMs(text: string): number {
  let reveal = 0;
  for (let i = 0; i < text.length; i++) reveal += ALNUM.test(text[i]) ? CHAR_MS : PUNCT_MS;
  return reveal + computeHoldMs(text) + FADE_MS;
}

/**
 * The typewriter, split out so the bubble and the death card reveal at identical
 * cadence. Owns one timer and one text node; `finish()` snaps to complete and is what
 * preemption calls instead of racing a fade.
 */
export class Typewriter {
  readonly node: Text;
  private timer = 0;
  private full = '';
  private index = 0;
  private onDone: (() => void) | null = null;
  /** Reduced motion reveals whole: the line is information, not decoration. */
  instant = false;

  constructor(node: Text) {
    this.node = node;
  }

  get isRunning(): boolean {
    return this.timer !== 0;
  }

  start(text: string, onDone?: () => void): void {
    this.stop();
    this.full = text;
    this.index = 0;
    this.onDone = onDone ?? null;
    this.node.nodeValue = '';
    if (this.instant) {
      this.finish();
      return;
    }
    this.step();
  }

  /** Snaps to the full string and runs the completion callback exactly once. */
  finish(): void {
    if (this.timer !== 0) {
      clearTimeout(this.timer);
      this.timer = 0;
    }
    this.node.nodeValue = this.full;
    this.index = this.full.length;
    const done = this.onDone;
    this.onDone = null;
    if (done !== null) done();
  }

  /** Kills the timer and forgets the callback. Nothing is written. */
  stop(): void {
    if (this.timer !== 0) {
      clearTimeout(this.timer);
      this.timer = 0;
    }
    this.onDone = null;
  }

  clear(): void {
    this.stop();
    this.full = '';
    this.index = 0;
    this.node.nodeValue = '';
  }

  private step(): void {
    if (this.index >= this.full.length) {
      this.timer = 0;
      const done = this.onDone;
      this.onDone = null;
      if (done !== null) done();
      return;
    }
    const ch = this.full[this.index];
    this.index++;
    this.node.nodeValue = this.full.slice(0, this.index);
    const letter = ALNUM.test(ch);
    this.timer = setTimeout(() => this.step(), letter ? CHAR_MS : PUNCT_MS) as unknown as number;
  }
}

export class CaptainSpeech {
  readonly root: HTMLElement;

  private readonly plate: HTMLElement;
  private readonly type: Typewriter;
  private hideTimer = 0;
  private shown = false;
  private readonly face: HTMLCanvasElement;
  /** One buffer for the run; `setCaptain` repaints it in place when the captain changes. */
  private portrait: Portrait | null = null;
  private faceSeed = -1;

  constructor() {
    this.root = div('captain-bubble');
    this.root.setAttribute('aria-live', 'polite');
    this.root.hidden = true;

    /*
     * The face rides with the voice.
     *
     * `HUD_SCALE` has existed in `portrait.ts` since the portrait was written — "Run HUD.
     * Presence, not identity — the player should know somebody is in there" — and nothing has
     * ever spent it. The bubble said a name thirty times a run and never showed who was
     * saying it, while the only place the face appeared was the card you see once, after they
     * are dead. This is the surface that earns the recall the death card cashes in.
     *
     * Composed at 1x, 64 CSS px. The art pipeline's note is that 64 px carries presence but
     * not identity, and presence is exactly the job here: a small figure in a corner, glanced
     * at mid-fight. Identity stays the death card's, at 3x, where it is the whole point.
     */
    this.face = el('canvas', 'captain-face-sm');
    this.face.setAttribute('aria-hidden', 'true');

    const text = div('captain-text');
    this.plate = div('captain-plate', '');
    const line = div('captain-line');
    const body = span('captain-body');
    const node = document.createTextNode('');
    body.appendChild(node);
    line.appendChild(body);
    text.appendChild(this.plate);
    text.appendChild(line);

    this.root.appendChild(this.face);
    this.root.appendChild(text);
    this.type = new Typewriter(node);
  }

  /**
   * The captain whose face rides the bubble. Idempotent per captain: the portrait is composed
   * once and repainted in place, never per line and never per frame.
   */
  setCaptain(captain: Captain): void {
    const seed = captain.runSeed >>> 0;
    if (seed === this.faceSeed) return;
    this.faceSeed = seed;
    if (this.portrait === null) this.portrait = composePortrait(captain, { scale: 1 });
    else recompose(captain, this.portrait);
    paintPortraitToCanvas(this.portrait, this.face);
    // Nearest-neighbour upscaling only survives if the backing store and the CSS box agree
    // exactly; the death card learned this the same way.
    this.face.style.inlineSize = this.portrait.width + 'px';
    this.face.style.blockSize = this.portrait.height + 'px';
  }

  /** The name plate. Short form: `Okonkwo`, or the epithet when the crew gave one. */
  setSpeaker(shortName: string): void {
    this.plate.textContent = shortName;
  }

  setReducedMotion(on: boolean): void {
    this.type.instant = on;
  }

  /**
   * Says a line, replacing whatever is on screen. The outgoing line never fades — it
   * is snapped to complete and overwritten mid-spring, which is what keeps collisions
   * from producing either overlap or a dropped line. All pacing discipline lives
   * upstream in `CaptainVoice`; there is no queue here by design.
   */
  say(text: string): void {
    if (text.length === 0) return;
    if (this.type.isRunning) this.type.finish();
    if (this.hideTimer !== 0) {
      clearTimeout(this.hideTimer);
      this.hideTimer = 0;
    }

    this.root.hidden = false;
    this.shown = true;
    const el = this.root;
    el.style.opacity = '0';
    // `--captain-shift` is the horizontal anchor: -50% under the ship on a tall viewport,
    // 0 when the short-viewport rules pin the bubble to the inline-start edge. It has to be
    // a variable because these transforms are inline styles, which no stylesheet can override.
    el.style.transform = 'translate(var(--captain-shift, -50%), 6px) scale(0.82)';
    void el.offsetWidth; // forced reflow — without it both writes coalesce and nothing animates
    el.style.opacity = '1';
    el.style.transform = 'translate(var(--captain-shift, -50%), 0) scale(1)';

    this.type.start(text, () => {
      this.hideTimer = setTimeout(() => this.hide(), computeHoldMs(text)) as unknown as number;
    });
  }

  /** Fades out. The element stays in the DOM at opacity 0, as the source's does. */
  hide(): void {
    if (this.hideTimer !== 0) {
      clearTimeout(this.hideTimer);
      this.hideTimer = 0;
    }
    if (!this.shown) return;
    this.shown = false;
    this.root.style.opacity = '0';
    this.root.style.transform = 'translate(var(--captain-shift, -50%), -4px) scale(0.94)';
  }

  /**
   * Kills the typewriter and both timers with no fade. Called on `game_over` so a
   * half-typed ambient line cannot still be crawling across the death card, and on
   * run start so a new captain never inherits the last one's sentence.
   */
  stop(): void {
    this.type.clear();
    if (this.hideTimer !== 0) {
      clearTimeout(this.hideTimer);
      this.hideTimer = 0;
    }
    this.shown = false;
    this.root.hidden = true;
    this.root.style.opacity = '0';
  }

  dispose(): void {
    this.stop();
    if (this.root.parentNode !== null) this.root.parentNode.removeChild(this.root);
  }
}
